import { useState, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import axios from 'axios';

/**
 * Hook for CV template matching in the Plan Takeoff workspace.
 * Manages match execution, candidate state, and acceptance/rejection workflows.
 */
export default function useCvMatching({ url, projectId, takeoffId, addPlacement, activePage }) {
  const [matching, setMatching] = useState(false);
  const [matchItemId, setMatchItemId] = useState(null);
  const [candidates, setCandidates] = useState([]);

  const runMatch = useCallback(async (itemId, pageIndex, searchRegions, settings) => {
    setMatching(true);
    setMatchItemId(itemId);
    setCandidates([]);
    try {
      const res = await axios.post(
        `${url}/api/bid-estimation/projects/${projectId}/plan-takeoffs/${takeoffId}/match`,
        { itemId, pageIndex, searchRegions, settings }
      );
      const results = (res.data.candidates || []).map((c) => ({
        ...c,
        status: 'pending',
      }));
      setCandidates(results);
    } catch (err) {
      console.error('[useCvMatching] match request failed:', err);
      setCandidates([]);
    } finally {
      setMatching(false);
    }
  }, [url, projectId, takeoffId]);

  const acceptCandidate = useCallback((idx) => {
    setCandidates(prev => {
      const next = [...prev];
      if (!next[idx] || next[idx].status !== 'pending') return next;
      next[idx] = { ...next[idx], status: 'accepted' };
      const c = next[idx];
      const cx = c.x + (c.w || 0) / 2;
      const cy = c.y + (c.h || 0) / 2;
      addPlacement(matchItemId, {
        id: uuidv4(),
        page: activePage,
        pixelPath: [{ x: cx, y: cy }],
        measuredQty: 1,
        measuredUnit: 'ea',
      });
      return next;
    });
  }, [matchItemId, addPlacement, activePage]);

  const rejectCandidate = useCallback((idx) => {
    setCandidates((prev) => {
      const updated = [...prev];
      const candidate = updated[idx];
      if (!candidate || candidate.status !== 'pending') return prev;
      updated[idx] = { ...candidate, status: 'rejected' };
      return updated;
    });
  }, []);

  const acceptAllAbove = useCallback((threshold) => {
    setCandidates((prev) => {
      const updated = prev.map((candidate) => {
        if (candidate.status === 'pending' && candidate.confidence >= threshold) {
          const cx = candidate.x + (candidate.w || 0) / 2;
          const cy = candidate.y + (candidate.h || 0) / 2;
          addPlacement(matchItemId, {
            id: uuidv4(),
            page: activePage,
            pixelPath: [{ x: cx, y: cy }],
            measuredQty: 1,
            measuredUnit: 'ea',
          });
          return { ...candidate, status: 'accepted' };
        }
        return candidate;
      });
      return updated;
    });
  }, [matchItemId, addPlacement, activePage]);

  const rejectAllBelow = useCallback((threshold) => {
    setCandidates((prev) =>
      prev.map((candidate) => {
        if (candidate.status === 'pending' && candidate.confidence < threshold) {
          return { ...candidate, status: 'rejected' };
        }
        return candidate;
      })
    );
  }, []);

  const clearCandidates = useCallback(() => {
    setMatchItemId(null);
    setCandidates([]);
  }, []);

  return {
    matching,
    matchItemId,
    candidates,
    runMatch,
    acceptCandidate,
    rejectCandidate,
    acceptAllAbove,
    rejectAllBelow,
    clearCandidates,
  };
}
